import React, { useEffect } from 'react';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { get, useFieldArray, useFormContext } from 'react-hook-form';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useRoundingTFunction } from '@retail/rounding/i18n';
import { Add } from '@mui/icons-material';
import { TrashIcon } from '@sanity/icons';
import { PriceInput } from '../PriceInput';
import { PriceRoundingRuleConfigSchema } from './roundingRuleSchema';
import { RoundingModeInput } from './RoundingModeInput';
import { TargetEndingsInputCell } from './TargetEndingsInputCell';

type PriceRoundingRuleConfigSchemaWithId = PriceRoundingRuleConfigSchema & { id: string };

const partialInitRow = {
  priceTo: '',
};

export const CreateRoundingRuleSetForm: React.FC = () => {
  const t = useRoundingTFunction();
  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<PriceRoundingRuleConfigSchema>();
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'rules',
  });

  const lastRowPriceTo = watch(`rules.${fields.length - 1}.priceTo`);
  const lastRowPriceToError = get(errors, `rules.${fields.length - 1}.priceTo.message`);
  const lastRowIsValid = !lastRowPriceToError && !!lastRowPriceTo;
  const rulesListError = errors.rules?.message;

  const addNewRow = () => {
    const currentRules = getValues('rules');
    const currentLastIndex = fields.length - 1;
    const currentLastRowPriceTo = currentRules?.[currentLastIndex]?.priceTo;
    // @ts-ignore
    append({ priceFrom: currentLastRowPriceTo, ...partialInitRow });
  };

  const removeLastRow = () => {
    const currentLastIndex = fields.length - 1;
    const currentValues = getValues('rules');
    if (currentValues) {
      const updatedValues = currentValues.filter((_, i) => i !== currentLastIndex);
      replace(updatedValues);
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.startsWith('rules.') && name?.endsWith('.priceTo')) {
        const match = name.match(/rules\.(\d+)\.priceTo/);
        if (match) {
          const currentIndex = parseInt(match[1], 10);
          const nextIndex = currentIndex + 1;

          if (nextIndex < fields.length) {
            const newPriceTo = value.rules?.[currentIndex]?.priceTo;
            if (newPriceTo != null) {
              setValue(`rules.${nextIndex}.priceFrom`, newPriceTo, {
                shouldValidate: true,
              });
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, fields.length]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.startsWith('rules.') && name?.endsWith('.roundingMode')) {
        const match = name.match(/rules\.(\d+)\.roundingMode/);
        if (match) {
          const currentIndex = parseInt(match[1], 10);
          const currentRoundingMode = value.rules?.[currentIndex]?.roundingMode;
          if (currentRoundingMode === 'KEEP') {
            setValue(`rules.${currentIndex}.targetEndings`, undefined, {
              shouldValidate: true,
            });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  });

  const columns: GridColDef<PriceRoundingRuleConfigSchemaWithId>[] = [
    {
      field: 'priceFrom',
      headerName: t('rounding.priceFrom'),
      minWidth: 140,
      flex: 0.25,
      renderCell: ({ row, api }) => {
        const index = api.getRowIndexRelativeToVisibleRows(row.id);
        return (
          <PriceInput
            name={`rules.${index}.priceFrom`}
            control={control}
            size="xs"
            disabled
            sx={{ width: '120px' }}
          />
        );
      },
    },
    {
      field: 'priceTo',
      headerName: t('rounding.priceTo'),
      minWidth: 140,
      flex: 0.25,
      renderCell: ({ row, api }) => {
        const index = api.getRowIndexRelativeToVisibleRows(row.id);
        return <PriceInput name={`rules.${index}.priceTo`} control={control} size="xs" />;
      },
    },
    {
      field: 'roundingMode',
      headerName: t('rounding.roundingMode'),
      minWidth: 140,
      flex: 0.25,
      renderCell: ({ row, api }) => {
        const index = api.getRowIndexRelativeToVisibleRows(row.id);
        return <RoundingModeInput index={index} />;
      },
    },
    {
      field: 'targetEndings',
      headerName: t('rounding.targetEndings.title'),
      minWidth: 150,
      flex: 1,
      renderCell: ({ row, api }) => {
        const index = api.getRowIndexRelativeToVisibleRows(row.id);
        const roundingModeValue = watch(`rules.${index}.roundingMode`);
        switch (roundingModeValue) {
          case 'KEEP':
            return null;
          case 'ROUND_UP':
          case 'CLOSEST':
            return <TargetEndingsInputCell index={index} />;
          default:
            return (
              <Typography variant="body2" color="text.disabled">
                {t('rounding.targetEndings.chooseRuleFirst')}
              </Typography>
            );
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      renderCell: ({ row, api }) => {
        const isLastRow = api.getRowIndexRelativeToVisibleRows(row.id) === fields.length - 1;
        if (!isLastRow || fields.length - 1 === 0) return null;
        return (
          <Tooltip title={t('rounding.actions.removeRow')}>
            <IconButton onClick={removeLastRow}>
              <TrashIcon fontSize="25px" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <DataGridPremium<PriceRoundingRuleConfigSchemaWithId>
      columns={columns}
      // @ts-ignore
      rows={fields}
      getRowId={(row) => row.id}
      disableRowSelectionOnClick
      disableColumnResize
      disableColumnReorder
      disableColumnMenu
      disableColumnSorting
      getRowHeight={() => 'auto'}
      getEstimatedRowHeight={() => 70}
      slots={{
        footer: () => (
          <Stack m={2} direction="row" spacing={2}>
            <Tooltip
              title={t('rounding.actions.newRow.disabled')}
              disableHoverListener={lastRowIsValid}
            >
              <Button
                color="secondary"
                variant="contained"
                startIcon={<Add />}
                onClick={addNewRow}
                disabled={!lastRowIsValid}
              >
                {t('rounding.actions.newRow.title')}
              </Button>
            </Tooltip>
            {rulesListError && lastRowIsValid && (
              <Typography
                sx={(theme) => ({
                  color: theme.vars.palette.error.main,
                  backgroundColor: theme.vars.palette.semantic.tint.red.soft,
                  borderRadius: '8px',
                  borderColor: theme.vars.palette.error.light,
                  px: 2,
                  py: 1,
                })}
              >
                {rulesListError}
              </Typography>
            )}
          </Stack>
        ),
      }}
    />
  );
};
